/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor, dlsOverlay, dlsShadow, dlsZIndex} from '../../theme/theme';

export const SEARCH_OVERLAY_WIDTH = 608;

/**
 * Defines styles for the scrim-backed search overlay.
 *
 * ```typescript
 * import {useSearchOverlayStyles} from '@ion/dls/src/components/search/SearchOverlay.styles';
 *
 * ...
 *
 * const classes = useSearchOverlayStyles();
 * ```
 *
 * @returns Style classes for the overlay shell, search input, and results panel.
 */
export const useSearchOverlayStyles = makeStyles(theme => ({
  root : {
    alignItems                     : 'flex-start',
    display                        : 'flex',
    justifyContent                 : 'center',
    outline                        : 0,
    paddingTop                     : '25vh',
    zIndex                         : dlsZIndex('trackingSearchScrim'),
    [theme.breakpoints.down('sm')] : {
      alignItems : 'flex-start',
      padding    : '32px 16px 0',
      marginTop  : '24px'
    }
  },
  backdrop : {
    backgroundColor : dlsOverlay('black')
  },
  container : {
    display                        : 'flex',
    flexDirection                  : 'column',
    gap                            : 16,
    outline                        : 0,
    position                       : 'relative',
    width                          : SEARCH_OVERLAY_WIDTH,
    zIndex                         : dlsZIndex('trackingSearchOverlay'),
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  titleContainer : {
    alignItems                     : 'center',
    borderRadius                   : 12,
    display                        : 'flex',
    height                         : 22,
    padding                        : '0 16px',
    width                          : 191,
    [theme.breakpoints.down('sm')] : {
      display : 'none'
    }
  },
  title : {
    alignItems : 'center',
    color      : dlsColor('white'),
    display    : 'flex',
    fontFamily : 'Inter',
    fontSize   : 20,
    fontWeight : 700,
    lineHeight : '22px',
    textShadow : '0 0 10px rgba(0, 0, 0, 0.25)',
    whiteSpace : 'nowrap'
  },
  searchStack : {
    alignItems    : 'stretch',
    borderRadius  : 8,
    display       : 'flex',
    flexDirection : 'column',
    gap           : 16,
    width         : '100%'
  },
  searchBox : {
    alignItems       : 'center',
    backgroundColor  : dlsColor('white'),
    border           : `1px solid ${dlsColor('grey5')}`,
    borderRadius     : 8,
    boxShadow        : dlsShadow(2),
    boxSizing        : 'border-box',
    display          : 'flex',
    gap              : 49,
    height           : 44,
    padding          : 16,
    width            : '100%',
    '&:focus-within' : {
      border  : `2px solid ${dlsColor('darkTeal')}`,
      padding : 15
    }
  },
  inputGroup : {
    alignItems : 'center',
    display    : 'flex',
    flex       : 1,
    gap        : 8,
    height     : 24,
    minWidth   : 0
  },
  input : {
    backgroundColor  : 'transparent',
    border           : 0,
    color            : dlsColor('grey1'),
    flex             : 1,
    fontFamily       : 'Inter',
    fontSize         : 14,
    fontWeight       : 400,
    height           : 24,
    lineHeight       : '20px',
    minWidth         : 0,
    outline          : 0,
    padding          : 0,
    '&::placeholder' : {
      color   : dlsColor('grey1'),
      opacity : 1
    }
  },
  closeButton : {
    alignItems        : 'center',
    backgroundColor   : dlsColor('white'),
    border            : `1px solid ${dlsColor('grey5')}`,
    borderRadius      : 8,
    boxSizing         : 'border-box',
    cursor            : 'pointer',
    display           : 'flex',
    height            : 24,
    justifyContent    : 'center',
    padding           : 5,
    width             : 24,
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('darkTeal')}`,
      outlineOffset : 2
    }
  },
  // the 1px border is drawn inside the panel (an inset shadow), as Figma
  // draws it - a real border took 2px of the 342px height, which left the
  // 276px list 2px short, so five recent searches scrolled ...
  panel : {
    alignItems      : 'flex-start',
    backgroundColor : dlsColor('white'),
    borderRadius    : 12,
    boxShadow       : `${dlsShadow(2)}, inset 0 0 0 1px ${dlsColor('grey5')}`,
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    height          : 342,
    overflow        : 'hidden',
    width           : '100%'
  },
  panelHeader : {
    alignItems : 'center',
    boxSizing  : 'border-box',
    color      : dlsColor('grey3'),
    display    : 'flex',
    fontFamily : 'Inter',
    fontSize   : 16,
    fontWeight : 600,
    gap        : 8,
    height     : 66,
    lineHeight : '18px',
    padding    : '24px 16px',
    width      : '100%'
  },
  resultCount : {
    color      : dlsColor('grey3'),
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 400,
    lineHeight : '18px'
  },
  list : {
    alignItems    : 'flex-start',
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    gap           : 8,
    height        : 276,
    overflowY     : 'auto',
    padding       : '0 16px 24px',
    width         : '100%'
  },
  row : {
    alignItems    : 'center',
    display       : 'flex',
    flexDirection : 'row',
    gap           : 8,
    height        : 44,
    width         : '100%'
  },
  rowButton : {
    alignItems        : 'center',
    backgroundColor   : 'transparent',
    border            : 0,
    boxSizing         : 'border-box',
    color             : dlsColor('grey3'),
    cursor            : 'pointer',
    display           : 'flex',
    flex              : 1,
    gap               : 8,
    height            : 44,
    minWidth          : 0,
    padding           : '8px 0',
    textAlign         : 'left',
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('darkTeal')}`,
      outlineOffset : -2
    }
  },
  resultIcon : {
    alignItems     : 'center',
    display        : 'flex',
    height         : 24,
    justifyContent : 'center',
    width          : 24
  },
  resultText : {
    color        : dlsColor('grey3'),
    flex         : 1,
    fontFamily   : 'Inter',
    fontSize     : 14,
    fontWeight   : 400,
    lineHeight   : '20px',
    minWidth     : 0,
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap',
    '& #match'   : {
      fontWeight : 700
    },
    '& #associatematch' : {
      color      : dlsColor('caBlue'),
      fontWeight : 700
    }
  },
  removeButton : {
    alignItems        : 'center',
    backgroundColor   : 'transparent',
    border            : 0,
    borderRadius      : 8,
    cursor            : 'pointer',
    display           : 'flex',
    flexShrink        : 0,
    height            : 24,
    justifyContent    : 'center',
    padding           : 5,
    width             : 24,
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('darkTeal')}`,
      outlineOffset : 2
    }
  },
  stateText : {
    alignItems     : 'center',
    color          : dlsColor('grey3'),
    display        : 'flex',
    fontFamily     : 'Inter',
    fontSize       : 14,
    fontWeight     : 600,
    height         : 44,
    justifyContent : 'center',
    lineHeight     : '20px',
    width          : '100%'
  },
  loader : {
    alignItems     : 'center',
    display        : 'flex',
    flex           : 1,
    height         : '100%',
    justifyContent : 'center',
    width          : '100%'
  }
}) as any);
